import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LayoutDashboard, LogOut, Menu, Settings, X } from "lucide-react";
import formulaMark from "../assets/brand/logo.png";
import formulaIcon from "../assets/brand/logo-icon.png";
import { useAuth } from "../context/AuthContext";
import { roleHome } from "../utils/roleHome";

export const Container = ({ children, className = "" }) => (
  <div className={`site-shell ${className}`}>{children}</div>
);

export const Brand = ({ compact = false, icon = false }) => (
  icon ? (
    <span className={`brand icon ${compact ? "brand--compact" : ""}`}>
      <img src={formulaIcon} alt="Formula Soccer Center" />
    </span>
  ) : (
    <span className={`brand ${compact ? "brand--compact" : ""}`}>
      <img src={formulaMark} alt="Formula Soccer Center" />
    </span>
  )
);

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { isAuthenticated, user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const accountButtonRef = useRef(null);
  const accountHref = roleHome(user?.role);

  const links = [
    ["About Us", "/about"],
    ["Memberships", "/memberships"],
    ["Field Rentals", "/rentals"],
    ["Birthday Parties", "/#programs", true],
    ["Other Programs", "/programs"],
    ["Schedule", "/schedule"],
  ];

  useEffect(() => { setMenuOpen(false); setAccountOpen(false); }, [location.pathname, location.search, location.hash]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => { if (event.key === "Escape") { setMenuOpen(false); menuButtonRef.current?.focus(); } };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = overflow; document.removeEventListener("keydown", onKey); };
  }, [menuOpen]);
  useEffect(() => {
    if (!accountOpen) return undefined;
    const onPointer = (event) => { if (!headerRef.current?.contains(event.target)) setAccountOpen(false); };
    const onKey = (event) => { if (event.key === "Escape") { setAccountOpen(false); accountButtonRef.current?.focus(); } };
    document.addEventListener("pointerdown", onPointer); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [accountOpen]);
  const signOutNow = () => { setMenuOpen(false); setAccountOpen(false); signOut(); navigate("/", { replace: true }); };
  const closeMobile = () => { setMenuOpen(false); menuButtonRef.current?.focus(); };
  const navClass = ({ isActive }) => isActive ? "is-active" : undefined;

  return (
    <header className="topbar" ref={headerRef}>
      <Container className="topbar__inner">
        <Link to="/" aria-label="Formula Soccer home">
          <Brand compact icon />
        </Link>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {links.map(([label, href, anchor]) => anchor ? <Link to={href} key={label} className={location.pathname === "/" && location.hash === "#programs" ? "is-active" : undefined} aria-current={location.pathname === "/" && location.hash === "#programs" ? "page" : undefined}>{label}</Link> : <NavLink to={href} key={label} className={navClass}>{label}</NavLink>)}
          {!isAuthenticated ? <NavLink to="/login" className={navClass}>Sign In</NavLink> : <div className="site-account"><button ref={accountButtonRef} type="button" className="site-account-trigger" aria-expanded={accountOpen} aria-controls="site-account-menu" onClick={() => setAccountOpen((value) => !value)}><span className="site-account-avatar" aria-hidden="true">{user?.avatarUrl ? <img className="avatar-image" src={user.avatarUrl} alt="" /> : `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase()}</span><span>{user?.firstName || "Account"}</span><ChevronDown size={14} /></button>{accountOpen && <div className="site-account-menu" id="site-account-menu"><span className="site-account-identity">{user?.email}</span><Link to={accountHref}><LayoutDashboard size={16} /> Role home</Link><Link to="/settings"><Settings size={16} /> Settings</Link><button type="button" onClick={signOutNow}><LogOut size={16} /> Sign out</button></div>}</div>}
        </nav>
        <div className="topbar__contact">
          <a href="tel:+17476889668">+1 (747) 688-9668</a>
          <a href="mailto:info@formulasoccer.com">info@formulasoccer.com</a>
          <a
            href="https://maps.google.com/?q=15001+Calvert+Street+Van+Nuys+CA+91411"
            target="_blank"
            rel="noreferrer"
          >
            15001 Calvert St, Van Nuys
          </a>
        </div>
        <button
          ref={menuButtonRef}
          className="menu-button"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
        >
          {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} strokeWidth={1.25} aria-hidden="true" />}
        </button>
      </Container>
      <div
        className={`mobile-menu ${menuOpen ? "is-open" : ""}`}
        id="mobile-menu"
        hidden={!menuOpen}
      >
        <Container>
          {links.map(([label, href, anchor]) => anchor ? <Link to={href} key={label} onClick={closeMobile} className={location.pathname === "/" && location.hash === "#programs" ? "is-active" : undefined}>{label}</Link> : <NavLink to={href} key={label} onClick={closeMobile} className={navClass}>{label}</NavLink>)}
          {isAuthenticated ? <><Link to={accountHref} onClick={closeMobile}>Role home</Link><Link to="/settings" onClick={closeMobile}>Settings</Link><button type="button" onClick={signOutNow}>Sign out</button></> : <Link to="/login" onClick={closeMobile}>Sign In</Link>}
          <a href="tel:+17476889668">+1 (747) 688-9668</a>
          <a href="mailto:info@formulasoccer.com">info@formulasoccer.com</a>
        </Container>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer>
      <Container>
        <div className="footer-grid">
          <div>
            <Brand />
            <p>
              Objective performance tracking and player development, ages 2 to
              18 and beyond.
            </p>
          </div>
          <div>
            <h3>Programs</h3>
            <Link to="/#assessments">Assessments</Link>
            <Link to="/memberships">Memberships</Link>
            <Link to="/memberships">Private Access</Link>
            <Link to="/events">Camps & Leagues</Link>
          </div>
          <div>
            <h3>Company</h3>
            <Link to="/about">Formula Center</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/careers">Careers</Link>
          </div>
          <div>
            <h3>Follow</h3>
            <a href="https://www.instagram.com/" target="_blank" rel="noreferrer">Instagram</a>
            <a href="https://www.facebook.com/" target="_blank" rel="noreferrer">Facebook</a>
            <a href="https://www.tiktok.com/" target="_blank" rel="noreferrer">TikTok</a>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 Formula Soccer Center</span>
          <span>15001 Calvert Street, Van Nuys, CA 91411</span>
          <span className="footer-legal">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms & Conditions</Link>
            <Link to="/payments">Payments</Link>
            <Link to="/login">Parent Portal</Link>
            <Link to="/admin/login">Staff Sign In</Link>
          </span>
        </div>
      </Container>
    </footer>
  );
}
